import type { Drawing, Player } from '@/types'
import type { BaseCanvasActions, BaseCanvasCallbacks, BaseCanvasState } from './BaseCanvas.types'
import { PlayerLabelDialog } from '@/components/player'
import { DrawingEditorDialog } from './DrawingEditorDialog'

interface CanvasDialogsProps {
	state: BaseCanvasState
	actions: BaseCanvasActions
	players: Player[]
	drawings: Drawing[]
	callbacks: BaseCanvasCallbacks
	showDrawingEditor?: boolean
}

/**
 * Shared dialog rendering component for all canvas types.
 * Consolidates PlayerLabelDialog and DrawingEditorDialog logic.
 */
export function CanvasDialogs({
								  state,
								  actions,
								  players,
								  drawings,
								  callbacks,
								  showDrawingEditor = true,
							  }: CanvasDialogsProps) {
	const selectedPlayer = state.selectedPlayerId && Array.isArray(players)
		? players.find((p) => p.id === state.selectedPlayerId)
		: null

	// Check if selected player has a linked drawing
	const hasLinkedDrawing = showDrawingEditor && Array.isArray(drawings)
		? drawings.some((d) => d.playerId === state.selectedPlayerId)
		: false

	// Handle unlinking player from drawing — delegates to BaseCanvas action
	// which handles nudging the control point away and persisting
	const handleUnlink = () => {
		if (!showDrawingEditor || !state.selectedPlayerId) return
		actions.onUnlinkDrawing(state.selectedPlayerId)
	}

	// Handle player deletion
	const handleDelete = () => {
		if (state.selectedPlayerId) {
			callbacks.onPlayerDelete(state.selectedPlayerId)
			actions.setShowLabelDialog(false)
			actions.setSelectedPlayerId(null)
		}
	}

	return (
		<>
			{/* Player Label Dialog */}
			{state.showLabelDialog && selectedPlayer && (
				<PlayerLabelDialog
					position={state.labelDialogPosition}
					currentLabel={selectedPlayer.label ?? ''}
					hasLinkedDrawing={hasLinkedDrawing}
					onLabelChange={(label) => {
						callbacks.onPlayerLabelChange(state.selectedPlayerId!, label)
					}}
					onUnlink={handleUnlink}
					onDelete={handleDelete}
					onClose={() => actions.setShowLabelDialog(false)}
				/>
			)}

			{/* Drawing Properties Dialog - only for PlayCanvas and ConceptCanvas */}
			{showDrawingEditor && (
				<DrawingEditorDialog
					editingDrawing={state.editingDrawing}
					drawings={drawings}
					coordSystem={state.coordSystem}
					onDrawingUpdate={callbacks.onDrawingUpdate}
					onPreSnapUpdate={callbacks.onPreSnapUpdate}
					onClose={() => actions.setEditingDrawing(null)}
				/>
			)}
		</>
	)
}
